import * as THREE from 'three';
import { RECALL_TICKS, type HeroState } from '@moba/shared';
import { makeGlowTexture } from './assets.ts';

const FADE_IN = 0.25;
const FADE_OUT = 0.18;
const MOTES = 26;
const ARC_SEGMENTS = 72;

let sigilTexture: THREE.CanvasTexture | null = null;
let glowTexture: THREE.CanvasTexture | null = null;

/**
 * Sello del regreso a base, blanco sobre transparente (el material lo tiñe del
 * color del equipo): doble anillo con runas, estrella de seis puntas y círculo
 * interior, con un halo suave.
 */
function makeSigilTexture(size = 512): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  const r = size * 0.47;
  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  ctx.lineCap = 'round';
  ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
  ctx.shadowBlur = size * 0.015;

  const circle = (radius: number, width: number, alpha = 1): void => {
    ctx.globalAlpha = alpha;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.arc(c, c, radius, 0, Math.PI * 2);
    ctx.stroke();
  };

  circle(r, size * 0.016);
  circle(r * 0.86, size * 0.008, 0.9);

  // banda de runas: rombos y marcas alternos entre los dos anillos
  const runes = 24;
  for (let i = 0; i < runes; i++) {
    const a = (i / runes) * Math.PI * 2;
    const mid = r * 0.93;
    ctx.save();
    ctx.translate(c + Math.cos(a) * mid, c + Math.sin(a) * mid);
    ctx.rotate(a + Math.PI / 2);
    ctx.globalAlpha = 0.9;
    if (i % 2 === 0) {
      const s = size * 0.014;
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.lineTo(s * 0.6, 0);
      ctx.lineTo(0, s);
      ctx.lineTo(-s * 0.6, 0);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.lineWidth = size * 0.005;
      ctx.beginPath();
      ctx.moveTo(-size * 0.012, 0);
      ctx.lineTo(size * 0.012, 0);
      ctx.moveTo(0, -size * 0.01);
      ctx.lineTo(0, size * 0.01);
      ctx.stroke();
    }
    ctx.restore();
  }

  // estrella de seis puntas (dos triángulos) inscrita en el anillo interior
  ctx.globalAlpha = 0.9;
  ctx.lineWidth = size * 0.009;
  const star = r * 0.78;
  for (let t = 0; t < 2; t++) {
    ctx.beginPath();
    for (let k = 0; k <= 3; k++) {
      const a = -Math.PI / 2 + t * Math.PI + (k * Math.PI * 2) / 3;
      const x = c + Math.cos(a) * star;
      const y = c + Math.sin(a) * star;
      if (k === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  circle(star * 0.5, size * 0.006, 0.8);
  circle(star * 0.2, size * 0.004, 0.6);

  // halo interior suave
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  const halo = ctx.createRadialGradient(c, c, 0, c, c, r);
  halo.addColorStop(0, 'rgba(255,255,255,0.28)');
  halo.addColorStop(0.6, 'rgba(255,255,255,0.08)');
  halo.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fill();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

interface Mote {
  sprite: THREE.Sprite;
  angle: number;
  speed: number;
  phase: number;
  size: number;
}

/**
 * Canalización del regreso a base (B): un sello mágico del color del equipo
 * que gira en el suelo, un arco que se va cerrando con el progreso y motas de
 * luz que suben en espiral alrededor del héroe, cada vez más rápido.
 */
export class RecallFx {
  readonly group = new THREE.Group();
  private readonly sigil: THREE.Mesh;
  private readonly sigilMaterial: THREE.MeshBasicMaterial;
  private readonly arc: THREE.Mesh;
  private readonly arcMaterial: THREE.MeshBasicMaterial;
  private readonly glow: THREE.Sprite;
  /** disco oscuro bajo el sello: da contraste sobre el suelo claro */
  private readonly shade: THREE.Mesh;
  private readonly shadeMaterial: THREE.MeshBasicMaterial;
  private readonly motes: Mote[] = [];
  private readonly radius: number;
  private readonly height: number;
  private strength = 0;
  private spin = 0;
  private time = 0;
  private lastProgress = 0;

  constructor(heroRadius: number, heroHeight: number, color: number) {
    sigilTexture ??= makeSigilTexture();
    glowTexture ??= makeGlowTexture();
    this.radius = Math.max(1.25, heroRadius * 3.2);
    this.height = heroHeight;
    const tint = new THREE.Color(color);
    const light = tint.clone().lerp(new THREE.Color(0xffffff), 0.55);

    this.shadeMaterial = new THREE.MeshBasicMaterial({
      map: glowTexture,
      color: 0x02060c,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.shade = new THREE.Mesh(new THREE.PlaneGeometry(this.radius * 2.6, this.radius * 2.6), this.shadeMaterial);
    this.shade.rotation.x = -Math.PI / 2;
    this.shade.position.y = 0.03;
    this.shade.renderOrder = 1;
    this.group.add(this.shade);

    this.sigilMaterial = new THREE.MeshBasicMaterial({
      map: sigilTexture,
      color: tint,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.sigil = new THREE.Mesh(new THREE.PlaneGeometry(this.radius * 2, this.radius * 2), this.sigilMaterial);
    this.sigil.rotation.x = -Math.PI / 2;
    this.sigil.position.y = 0.035;
    this.sigil.renderOrder = 2;
    this.group.add(this.sigil);

    // arco de progreso justo por fuera del sello (drawRange = progreso)
    this.arcMaterial = new THREE.MeshBasicMaterial({
      color: light,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const arcGeometry = new THREE.RingGeometry(this.radius * 1.03, this.radius * 1.15, ARC_SEGMENTS, 1, Math.PI / 2, -Math.PI * 2);
    this.arc = new THREE.Mesh(arcGeometry, this.arcMaterial);
    this.arc.rotation.x = -Math.PI / 2;
    this.arc.position.y = 0.04;
    this.arc.renderOrder = 3;
    this.group.add(this.arc);

    this.glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture,
        color: tint,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.glow.position.y = heroHeight * 0.45;
    this.glow.scale.setScalar(heroHeight * 1.6);
    this.group.add(this.glow);

    for (let i = 0; i < MOTES; i++) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTexture,
          color: i % 3 === 0 ? 0xffffff : light,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      this.group.add(sprite);
      this.motes.push({
        sprite,
        angle: (i / MOTES) * Math.PI * 2,
        speed: 0.45 + ((i * 37) % 11) / 20,
        phase: ((i * 53) % 17) / 17,
        size: 0.28 + ((i * 29) % 7) / 30,
      });
    }
    this.group.visible = false;
  }

  update(dt: number, hero: HeroState): void {
    const ticks = hero.alive ? hero.recallTicks : 0;
    const active = ticks > 0;
    if (active) this.lastProgress = Math.min(1, ticks / RECALL_TICKS);
    this.strength = active
      ? Math.min(1, this.strength + dt / FADE_IN)
      : Math.max(0, this.strength - dt / FADE_OUT);
    this.group.visible = this.strength > 0;
    if (!this.group.visible) return;

    const progress = this.lastProgress;
    const s = this.strength;
    // al final de la canalización todo se acelera y brilla más
    const surge = progress * progress;
    this.time += dt;
    this.spin += dt * (0.5 + surge * 2.5);

    const pop = 0.55 + 0.45 * easeOutBack(s);
    this.sigil.scale.setScalar(pop);
    this.sigil.rotation.z = this.spin;
    this.sigilMaterial.opacity = s * Math.min(1, 0.85 + 0.15 * surge + 0.08 * Math.sin(this.time * 6));
    this.shade.scale.setScalar(pop);
    this.shadeMaterial.opacity = s * 0.55;

    this.arc.scale.setScalar(pop);
    this.arc.geometry.setDrawRange(0, Math.floor(progress * ARC_SEGMENTS) * 6);
    this.arcMaterial.opacity = s * 0.95;

    (this.glow.material as THREE.SpriteMaterial).opacity = s * (0.12 + 0.3 * surge);

    for (const mote of this.motes) {
      // cada mota sube del sello en espiral y vuelve a empezar al llegar arriba
      const rise = (mote.phase + this.time * mote.speed * (0.6 + surge * 1.6)) % 1;
      const angle = mote.angle + this.spin * 1.6 + rise * Math.PI * 1.5;
      const r = this.radius * 0.85 * (1 - rise * 0.65) * pop;
      mote.sprite.position.set(Math.cos(angle) * r, 0.1 + rise * this.height * 1.25, Math.sin(angle) * r);
      mote.sprite.scale.setScalar(mote.size * (1 - rise * 0.5) * (1 + surge * 0.6));
      const fade = Math.min(1, rise * 6) * (1 - rise);
      (mote.sprite.material as THREE.SpriteMaterial).opacity = s * fade * (0.8 + 0.2 * surge);
    }
  }
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
}
